import { Injectable } from "@angular/core";
import { ItemDataService } from "../item-data.service";
import { ItemData, Tier, TieredItem, TierList, TierValue, TierValueList } from "../../models/item-data-models";
import { Filter, FavouriteCurrencyMode } from "../../models/filter-models";
import { CurrencyTieringMode } from "../../models/enums";
import { poe1, poe2 } from "../../../game";
import { logError } from "../../app.config";
import { EconomyService } from "../economy.service";
import { PriceType } from "../../models/economy-models";
import { formatTierThresholdValue, getEffectiveTierThresholdValue } from "../../tier-list-tab/tier-value-helpers";

export const ORDERED_TIERS = [Tier.EVisible, Tier.DWhite, Tier.CYellow, Tier.BOrange, Tier.ARed, Tier.SPurple];

// When crossing from > 1 to < 1 in the same currency, show ~ 1 only if the tier band
// [threshold, previous threshold] is narrow enough to sit around one unit (e.g. 0.5–2 Chaos).
const TILDE_ONE_MAX_BAND_RATIO = 8;

export function currencyTierRank(tier: Tier): number {
  if (tier === Tier.FHidden) return -1;
  return ORDERED_TIERS.indexOf(tier);
}

export function pickHigherCurrencyTier(a: Tier, b: Tier): Tier {
  return currencyTierRank(a) >= currencyTierRank(b) ? a : b;
}

export function promoteCurrencyTier(tier: Tier, steps: number): Tier {
  const newRank = Math.min(currencyTierRank(tier) + steps, ORDERED_TIERS.length - 1);
  return ORDERED_TIERS[Math.max(newRank, 0)];
}

@Injectable({
  providedIn: 'root'
})
export class CurrencyTierService {
  private allItemData: ItemData[];
  private tieredCurrencyItemTypes: string[];

  private defaultTieredCurrency!: TieredItem[];

  private itemsByTieredCurrencyItemType: { baseType: string, itemType: string }[] = [];
  private itemsByCurrencyItemType: { baseType: string, itemType: string }[] = [];

  private tieredCurrency: TierList = [];

  private tierCutoffValues: {
    mode: CurrencyTieringMode,
    values: TierValueList
  }[] = poe2 ? [
    // ********* POE 2 *********
    {
      mode: 'Early',
      values: [
        { tier: Tier.SPurple, value: 20, baseCurrency: 'Exalted Orb' },
        { tier: Tier.ARed, value: 2.5, baseCurrency: 'Exalted Orb' },
        { tier: Tier.BOrange, value: 0.75, baseCurrency: 'Exalted Orb' },
        { tier: Tier.CYellow, value: 0.2, baseCurrency: 'Exalted Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Exalted Orb' },
        { tier: Tier.EVisible, value: 0.001, baseCurrency: 'Exalted Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Exalted Orb' }
      ]
    },
    {
      mode: 'Normal',
      values: [
        { tier: Tier.SPurple, value: 100, baseCurrency: 'Exalted Orb' },
        { tier: Tier.ARed, value: 15, baseCurrency: 'Exalted Orb' },
        { tier: Tier.BOrange, value: 3, baseCurrency: 'Exalted Orb' },
        { tier: Tier.CYellow, value: 0.5, baseCurrency: 'Exalted Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Exalted Orb' },
        { tier: Tier.EVisible, value: 0.01, baseCurrency: 'Exalted Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Exalted Orb' }
      ]
    },
    {
      mode: 'Strict',
      values: [
        { tier: Tier.SPurple, value: 3, baseCurrency: 'Divine Orb' },
        { tier: Tier.ARed, value: 1, baseCurrency: 'Divine Orb' },
        { tier: Tier.BOrange, value: 2, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.05, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'VeryStrict',
      values: [
        { tier: Tier.SPurple, value: 10, baseCurrency: 'Divine Orb' },
        { tier: Tier.ARed, value: 3, baseCurrency: 'Divine Orb' },
        { tier: Tier.BOrange, value: 1, baseCurrency: 'Divine Orb' },
        { tier: Tier.CYellow, value: 2, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    }
  ] : [
    // ********* POE 1 *********
    {
      mode: 'Early',
      values: [
        { tier: Tier.SPurple, value: 100, baseCurrency: 'Chaos Orb' },
        { tier: Tier.ARed, value: 15, baseCurrency: 'Chaos Orb' },
        { tier: Tier.BOrange, value: 3, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.01, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'Normal',
      values: [
        { tier: Tier.SPurple, value: 150, baseCurrency: 'Chaos Orb' },
        { tier: Tier.ARed, value: 25, baseCurrency: 'Chaos Orb' },
        { tier: Tier.BOrange, value: 5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 1.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.75, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'Strict',
      values: [
        { tier: Tier.SPurple, value: 1, baseCurrency: 'Divine Orb' },
        { tier: Tier.ARed, value: 50, baseCurrency: 'Chaos Orb' },
        { tier: Tier.BOrange, value: 10, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'VeryStrict',
      values: [
        { tier: Tier.SPurple, value: 10, baseCurrency: 'Divine Orb' },
        { tier: Tier.ARed, value: 2, baseCurrency: 'Divine Orb' },
        { tier: Tier.BOrange, value: 0.75, baseCurrency: 'Divine Orb' },
        { tier: Tier.CYellow, value: 10, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 2.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.75, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    }
  ];

  private currencyTierValueList: TierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;

  static cacheBreaker = 0;

  constructor(private itemDataService: ItemDataService, private economyService: EconomyService) {
    this.allItemData = this.itemDataService.getItemData();
    this.tieredCurrencyItemTypes = this.itemDataService.getTieredCurrencyItemTypes();

    this.buildItemsByTieredCurrencyItemType();
    this.buildItemsByCurrencyItemType();
  }

  buildItemsByCurrencyItemType() {
    const items: { baseType: string, itemType: string }[] = [];

    this.allItemData.forEach(item => {
      item.baseTypes.forEach(baseType => {
        items.push({ baseType: baseType.name, itemType: item.itemType });
      });
    });

    this.itemsByCurrencyItemType = items;
  }

  buildItemsByTieredCurrencyItemType() {
    const itemsByTieredCurrencyItemType: { baseType: string, itemType: string }[] = [];

    this.allItemData.forEach(item => {
      item.baseTypes.forEach(baseType => {
        itemsByTieredCurrencyItemType.push({ baseType: baseType.name, itemType: item.tierListItemType || item.itemType });
      });
    });

    this.itemDataService.getTierListItemTypeManual().forEach(item => {
      if (itemsByTieredCurrencyItemType.find(i => i.baseType === item.baseType)) {
        itemsByTieredCurrencyItemType.find(i => i.baseType === item.baseType)!.itemType = item.tierListItemType;
      } else {
        itemsByTieredCurrencyItemType.push({ baseType: item.baseType, itemType: item.tierListItemType });
      }
    });

    this.itemsByTieredCurrencyItemType = itemsByTieredCurrencyItemType;
  }

  buildDefaultTieredItems() {
    const currentValues = structuredClone(this.economyService.getCurrencyData());
    const previousValues = structuredClone(this.economyService.getPreviousCurrencyData());

    const exaltValue = poe2 ? 1 : (currentValues.find(item => item.name === 'Exalted Orb')?.price ?? 1);
    const chaosValue = poe2 ? currentValues.find(item => item.name === 'Chaos Orb')?.price ?? 10 : 1;
    const divineValue = currentValues.find(item => item.name === 'Divine Orb')?.price ?? 200;

    const prevExaltValue = poe2 ? 1 : (previousValues.find(item => item.name === 'Exalted Orb')?.price ?? exaltValue);
    const prevChaosValue = poe2 ? previousValues.find(item => item.name === 'Chaos Orb')?.price ?? chaosValue : 1;
    const prevDivineValue = previousValues.find(item => item.name === 'Divine Orb')?.price ?? divineValue;

    const toBaseValue = (threshold: TierValue): number => {
      return getEffectiveTierThresholdValue(threshold) * (threshold.baseCurrency === 'Chaos Orb' ? chaosValue : threshold.baseCurrency === 'Divine Orb' ? divineValue : exaltValue);
    }

    const toPreviousBaseValue = (threshold: TierValue): number => {
      return getEffectiveTierThresholdValue(threshold) * (threshold.baseCurrency === 'Chaos Orb' ? prevChaosValue : threshold.baseCurrency === 'Divine Orb' ? prevDivineValue : prevExaltValue);
    }

    this.defaultTieredCurrency = currentValues.map(item => {
      const tier = this.currencyTierValueList.filter(threshold => (item.price || 0) >= toBaseValue(threshold))[0]?.tier;

      const previousValue = previousValues.find(v => v.name === item.name)?.price;
      const previousTier = previousValue ? this.currencyTierValueList.filter(threshold => previousValue >= toPreviousBaseValue(threshold))[0]?.tier : undefined;
      const movedDown = previousTier ? ORDERED_TIERS.indexOf(previousTier) - ORDERED_TIERS.indexOf(tier) : 0;
      const movedUp = previousTier ? ORDERED_TIERS.indexOf(tier) - ORDERED_TIERS.indexOf(previousTier) : 0;

      return {
        name: item.name,
        value: item.price,
        tier,
        priceType: item.type,
        newValue: item.type === PriceType.NewValue,
        previousValue,
        previousTier,
        movedUp,
        movedDown,
      } as TieredItem;
    });

    this.itemDataService.getMinimalCurrencyTierListData().forEach(minimalItemWithTier => {
      const existingItem = this.defaultTieredCurrency.find(item => item.name === minimalItemWithTier.name);
      if (existingItem) {
        if (ORDERED_TIERS.indexOf(existingItem.tier as Tier) < ORDERED_TIERS.indexOf(minimalItemWithTier.tier as Tier)) {
          existingItem.tier = minimalItemWithTier.tier;
          existingItem.manualTier = true;
        }
      } else {
        minimalItemWithTier.manualTier = true;
        this.defaultTieredCurrency.push(minimalItemWithTier);
      }
    });
  }

  combineCurrencyTierListData(filter: Filter | null, simulate?: boolean): TieredItem[] {
    const defaultTieredItems = this.defaultTieredCurrency.map(i => ({ ...i } as TieredItem));

    if (simulate) return defaultTieredItems;

    this.applyFavouriteCurrencyTiering(filter!, defaultTieredItems);

    filter!.customCurrencyTiers.forEach(customItem => {
      const existingItem = defaultTieredItems.find(item => item.name === customItem.name);
      if (existingItem) {
        const economyTier = existingItem.tier;
        existingItem.originalTier = economyTier;
        existingItem.tier = customItem.shownExplicitly
          ? pickHigherCurrencyTier(economyTier, customItem.tier)
          : customItem.tier;
        if (!customItem.shownExplicitly) {
          delete existingItem.favouriteCurrencyAdjusted;
        }
      } else {
        const economyTier = Tier.EVisible;
        const item = {
          name: customItem.name,
          tier: customItem.shownExplicitly
            ? pickHigherCurrencyTier(economyTier, customItem.tier)
            : customItem.tier,
          noValue: true,
          originalTier: economyTier
        } as TieredItem;
        defaultTieredItems.push(item);
      }
    });

    const hiddenCurrency = this.getHiddenCurrency(filter!);
    const hiddenRunes = this.getHiddenRunes(filter!);
    const hiddenUncutGems = this.getHiddenUncutGems(filter!);

    defaultTieredItems.forEach(item => {
      if (hiddenCurrency.includes(item.name) || hiddenRunes.includes(item.name) || hiddenUncutGems.includes(item.name)) {
        item.tier = Tier.FHidden;
        item.hiddenByQuickFilter = true;
      }
    });

    const tierList: TierList = [
      { tier: Tier.SPurple, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) },
      { tier: Tier.ARed, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) },
      { tier: Tier.BOrange, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) },
      { tier: Tier.CYellow, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) },
      { tier: Tier.DWhite, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) },
      { tier: Tier.EVisible, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) },
      { tier: Tier.FHidden, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) }
    ];

    for (let type of this.itemDataService.getTieredCurrencyItemTypes()) {
      let items: string[] = this.itemsByTieredCurrencyItemType.filter(i => i.itemType === type).map(i => i.baseType);

      if (poe1 && filter?.ruthless) {
        items = items.filter(name => !this.itemDataService.getRuthlessDisabledCurrencyItems().includes(name));
      } else if (poe1) {
        items = items.filter(name => !this.itemDataService.getRuthlessOnlyCurrencyItems().includes(name));
      }

      items.forEach(itemName => {
        let item = defaultTieredItems.find(i => i.name === itemName) || { name: itemName, tier: Tier.ARed, noValue: true } as TieredItem;

        if (type === 'Divination Cards') {
          item.divCard = this.itemDataService.getDivinationCard(item.name);
        }

        let tierData = tierList.find(t => t.tier === item.tier);
        if (!tierData) {
          tierData = { tier: item.tier, itemTypes: this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] })) };
          tierList.push(tierData);
        }

        const itemData = this.allItemData.find(i => i.baseTypes.some(bt => bt.name === item.name));
        const customTierItemType = this.itemDataService.getTierListItemTypeManual().find(t => t.baseType === item.name)?.tierListItemType;
        const itemType = customTierItemType || itemData?.tierListItemType || itemData?.itemType || 'Other';

        let itemTypeGroup = tierData.itemTypes.find(it => it.itemType === itemType);
        if (!itemTypeGroup) {
          itemTypeGroup = { itemType: itemType, baseTypes: [] };
          tierData.itemTypes.push(itemTypeGroup);
        }

        itemTypeGroup.baseTypes.push(item);
      });
    }

    // Remove the item types for each tier that are empty

    tierList.forEach(tier => {
      tier.itemTypes = tier.itemTypes.filter(it => it.baseTypes.length > 0);
    });

    // Sort the baseTypes by value, decreasing
    tierList.forEach(tier => {
      tier.itemTypes.forEach(itemType => {
        itemType.baseTypes.sort((a, b) => {
          const valueA = a.value ?? 0;
          const valueB = b.value ?? 0;
          return valueB - valueA;
        });
      });
    });

    this.tieredCurrency.length = 0;
    this.tieredCurrency.push(...tierList);

    CurrencyTierService.cacheBreaker++;

    return defaultTieredItems;
  }

  setTieringMode(mode: CurrencyTieringMode, filter: Filter) {
    const valueList = mode === 'Custom' ? filter.tieringModeCustom : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList) {
      // Should never happen, but apparently sometimes does. Use defaul 'Normal'.
      logError('CurrencyTierService.setTieringMode: No value list found for mode ' + mode + '. Using default Normal.');
      this.currencyTierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;
    } else {
      this.currencyTierValueList = valueList;
    }
    // this.tieringMode = mode;
    filter.tieringMode = mode;
    this.buildDefaultTieredItems();
    this.combineCurrencyTierListData(filter);
  }

  getTierForPrice(price: number): Tier {
    const { exaltValue, chaosValue, divineValue } = this.getBaseCurrencyValues();
    const toBaseValue = (threshold: TierValue): number => {
      return getEffectiveTierThresholdValue(threshold) * (threshold.baseCurrency === 'Chaos Orb' ? chaosValue : threshold.baseCurrency === 'Divine Orb' ? divineValue : exaltValue);
    };
    return this.currencyTierValueList.filter(threshold => price >= toBaseValue(threshold))[0]?.tier ?? Tier.FHidden;
  }

  private getBaseCurrencyValues() {
    const currentValues = this.economyService.getCurrencyData();
    const exaltValue = poe2 ? 1 : (currentValues.find(item => item.name === 'Exalted Orb')?.price ?? 1);
    const chaosValue = poe2 ? currentValues.find(item => item.name === 'Chaos Orb')?.price ?? 10 : 1;
    const divineValue = currentValues.find(item => item.name === 'Divine Orb')?.price ?? 200;
    return { exaltValue, chaosValue, divineValue };
  }

  private applyFavouriteCurrencyTiering(filter: Filter, items: TieredItem[]) {
    const itemTypeByName = new Map(this.itemsByCurrencyItemType.map(i => [i.baseType, i.itemType]));

    items.forEach(item => {
      const candidates: Tier[] = [];

      if (filter.favouriteBasicCurrency && filter.favouriteBasicCurrencyTypes.includes(item.name)) {
        candidates.push(this.getFavouriteRuleTier(item.tier, item.value, filter.favouriteBasicCurrencyMode, filter.favouriteBasicCurrencyValue));
      }

      filter.favouriteCurrencyTypes.forEach(entry => {
        if (!entry.enabled) return;
        const itemType = itemTypeByName.get(item.name);
        if (!itemType || itemType === 'Currency') return;
        if (!entry.itemTypes.includes(itemType)) return;
        candidates.push(this.getFavouriteRuleTier(item.tier, item.value, entry.mode, entry.value));
      });

      if (candidates.length > 0) {
        const economyTier = item.tier;
        const newTier = candidates.reduce((best, tier) => pickHigherCurrencyTier(best, tier), economyTier as Tier);
        if (newTier !== economyTier) {
          item.favouriteCurrencyAdjusted = true;
        }
        item.tier = newTier;
      }
    });
  }

  private getFavouriteRuleTier(currentTier: Tier, itemValue: number | undefined, mode: FavouriteCurrencyMode, value: number | null): Tier {
    switch (mode) {
      case FavouriteCurrencyMode.Multiplier:
        if (value != null && itemValue != null) {
          return this.getTierForPrice(itemValue * value);
        }
        return currentTier;
      case FavouriteCurrencyMode.PromoteTier:
        return promoteCurrencyTier(currentTier, value ?? 1);
      case FavouriteCurrencyMode.ForceVisible:
        return pickHigherCurrencyTier(currentTier, Tier.EVisible);
    }
  }

  getDefaultTieredCurrency() {
    return this.defaultTieredCurrency;
  }

  getTierList() {
    return this.tieredCurrency;
  }

  getFirstCurrencyForDropTier(tier: Tier, divCard = false): string | undefined {
    const tierData = this.tieredCurrency.find(tl => tl.tier === tier);
    if (!tierData) return undefined;

    const baseTypes = divCard
      ? tierData.itemTypes.find(it => it.itemType === 'Divination Cards')?.baseTypes
      : tierData.itemTypes[0]?.baseTypes;

    return CurrencyTierService.pickRepresentativeBaseTypeName(baseTypes);
  }

  private static hasEconomyBasedPrice(item: TieredItem): boolean {
    if (item.noValue) return false;
    if (item.priceType == null) return true;
    return item.priceType !== PriceType.Manual
      && item.priceType !== PriceType.FixedBecauseMissing
      && item.priceType !== PriceType.LeagueBecauseMissing;
  }

  private static pickRepresentativeBaseTypeName(baseTypes: TieredItem[] | undefined): string | undefined {
    if (!baseTypes?.length) return undefined;

    const withEconomyPrice = baseTypes.filter(bt => CurrencyTierService.hasEconomyBasedPrice(bt));
    if (!withEconomyPrice.length) return undefined;

    const withoutApostrophe = withEconomyPrice.filter(bt => !bt.name.includes("'"));
    const candidates = withoutApostrophe.length > 0 ? withoutApostrophe : withEconomyPrice;
    return candidates[candidates.length - 1].name;
  }

  getSelectedTierValueList() {
    return this.currencyTierValueList;
  }

  getTierCutoffValues() {
    return this.tierCutoffValues;
  }

  getHiddenRunes(filter: Filter) {
    if (!filter.hideRegularRunes) return [];
    const allRegularRunes = this.itemDataService.getAllRegularRunes();
    const hiddenRunes = allRegularRunes.filter(r => filter.hideRegularRunesExceptions.indexOf(r) === -1);
    return hiddenRunes;
  }

  getHiddenUncutGems(filter: Filter) {
    if (!filter.hideUncutGems) return [];
    const hiddenUncutSkillGems = filter.hideUncutSkillGemsBelowLevel ? Array.from({ length: filter.hideUncutSkillGemsBelowLevel }, (_, i) => i + 1).map(i => `Uncut Skill Gem (Level ${i})`) : [];
    const hiddenUncutSpiritGems = filter.hideUncutSpiritGemsBelowLevel ? Array.from({ length: filter.hideUncutSpiritGemsBelowLevel - 3 }, (_, i) => i + 4).map(i => `Uncut Spirit Gem (Level ${i})`) : [];
    const hiddenUncutSupportGems = filter.hideUncutSupportGemsBelowLevel ? Array.from({ length: filter.hideUncutSupportGemsBelowLevel }, (_, i) => i + 1).map(i => `Uncut Support Gem (Level ${i})`) : [];
    const hiddenUncutGems = hiddenUncutSkillGems.concat(hiddenUncutSpiritGems).concat(hiddenUncutSupportGems);
    return hiddenUncutGems;
  }

  getHiddenCurrency(filter: Filter) {
    let hiddenCurrencyTypes = filter.hideCurrency ? filter.hideCurrencyTypes : [];
    if (filter.hideScrolls) hiddenCurrencyTypes = hiddenCurrencyTypes.concat('Scroll of Wisdom');
    if (poe1 && filter.hidePortalScrolls) hiddenCurrencyTypes = hiddenCurrencyTypes.concat('Portal Scroll');

    return hiddenCurrencyTypes;
  }

  unhideFromQuickFilter(filter: Filter, itemName: string): boolean {
    if (this.getHiddenCurrency(filter).includes(itemName)) {
      if (itemName === 'Scroll of Wisdom' && filter.hideScrolls) {
        filter.hideScrolls = false;
        if (poe1) filter.hideScrollsLowerThan = null;
      } else if (itemName === 'Portal Scroll' && filter.hidePortalScrolls) {
        filter.hidePortalScrolls = false;
        if (poe1) filter.hidePortalScrollsLowerThan = null;
      } else if (filter.hideCurrency && filter.hideCurrencyTypes.includes(itemName)) {
        filter.hideCurrencyTypes = filter.hideCurrencyTypes.filter(c => c !== itemName);
      }
      return true;
    }

    if (this.getHiddenRunes(filter).includes(itemName)) {
      if (!filter.hideRegularRunesExceptions.includes(itemName)) {
        const exceptions = filter.hideRegularRunesExceptions.filter(e => e !== 'None');
        filter.hideRegularRunesExceptions = [...exceptions, itemName];
      }
      return true;
    }

    if (this.getHiddenUncutGems(filter).includes(itemName)) {
      return this.unhideUncutGemFromQuickFilter(filter, itemName);
    }

    return false;
  }

  private unhideUncutGemFromQuickFilter(filter: Filter, itemName: string): boolean {
    const match = itemName.match(/Uncut (Skill|Spirit|Support) Gem \(Level (\d+)\)/);
    if (!match) return false;

    const gemType = match[1];
    const level = parseInt(match[2], 10);

    if (gemType === 'Skill' && filter.hideUncutSkillGemsBelowLevel && level <= filter.hideUncutSkillGemsBelowLevel) {
      filter.hideUncutSkillGemsBelowLevel = level > 1 ? level - 1 : null;
      return true;
    }
    if (gemType === 'Spirit' && filter.hideUncutSpiritGemsBelowLevel && level <= filter.hideUncutSpiritGemsBelowLevel) {
      filter.hideUncutSpiritGemsBelowLevel = level > 4 ? level - 1 : null;
      return true;
    }
    if (gemType === 'Support' && filter.hideUncutSupportGemsBelowLevel && level <= filter.hideUncutSupportGemsBelowLevel) {
      filter.hideUncutSupportGemsBelowLevel = level > 1 ? level - 1 : null;
      return true;
    }

    return false;
  }

  getDisplayValueForMode(mode: CurrencyTieringMode, tier: Tier, filter?: Filter | null) {
    const valueList = mode === 'Custom'
      ? filter?.tieringModeCustom
      : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList) return null;
    return this.getDisplayValue(tier, valueList);
  }

  getDisplayValue(tier: Tier, valueList: TierValueList = this.currencyTierValueList): { priceLabel: string, priceBaseCurrency: string, priceBaseCurrencyImage?: string } {
    const tierThreshold = valueList.find(t => t.tier === tier)!;
    let priceLabel = '≥ ' + formatTierThresholdValue(tierThreshold);
    let priceBaseCurrency = tierThreshold.baseCurrency;
    let priceBaseCurrencyImage = this.itemDataService.getFlatItemDataByBaseTypeName(priceBaseCurrency)?.baseType.image;

    const currentTierIndex = valueList.findIndex(t => t.tier === tier);
    const previousTierThreshold = currentTierIndex >= 0 && currentTierIndex - 1 >= 0
      ? valueList[currentTierIndex - 1]
      : undefined;

    // Same currency, step down from > 1 to < 1: show ~ 1 when the band up to the previous
    // threshold is narrow around one unit (e.g. 0.5–2 Chaos). Very wide bands (e.g. 0.4–10) keep ≥.
    if (previousTierThreshold && this.showsTildeOneAtCross(previousTierThreshold, tierThreshold)) {
      priceLabel = '~ 1';
    }

    // Upper-bound labels (< previous threshold) only after a ~ 1 row somewhere above, except F-tier which is always "< E threshold".
    const priorHasTildeOne = this.anyPriorTierShowsTildeOne(currentTierIndex, valueList);
    const tierEffective = getEffectiveTierThresholdValue(tierThreshold);
    const previousEffective = previousTierThreshold ? getEffectiveTierThresholdValue(previousTierThreshold) : undefined;
    const useLessThanPrevious =
      !!previousTierThreshold
      && (tier === Tier.FHidden
        || (previousTierThreshold.baseCurrency === tierThreshold.baseCurrency
          && tierEffective < 1
          && previousEffective! < 1
          && priorHasTildeOne));

    if (useLessThanPrevious) {
      priceLabel = '< ' + formatTierThresholdValue(previousTierThreshold!);
      priceBaseCurrency = previousTierThreshold!.baseCurrency;
      priceBaseCurrencyImage = this.itemDataService.getFlatItemDataByBaseTypeName(priceBaseCurrency)?.baseType.image;
    }

    return {
      priceLabel,
      priceBaseCurrency,
      priceBaseCurrencyImage
    };
  }

  private showsTildeOneAtCross(previousTierThreshold: TierValue, tierThreshold: TierValue): boolean {
    const previousEffective = getEffectiveTierThresholdValue(previousTierThreshold);
    const tierEffective = getEffectiveTierThresholdValue(tierThreshold);
    return previousTierThreshold.baseCurrency === tierThreshold.baseCurrency
      && tierEffective < 1
      && previousEffective > 1
      && previousEffective / tierEffective <= TILDE_ONE_MAX_BAND_RATIO;
  }

  /** Row at index shows ~ 1 when stepping from > 1 to < 1 in a band narrow around one unit. */
  private tierRowShowsTildeOne(rowIndex: number, values: TierValueList = this.currencyTierValueList): boolean {
    if (rowIndex <= 0 || rowIndex >= values.length) return false;
    return this.showsTildeOneAtCross(values[rowIndex - 1], values[rowIndex]);
  }

  private anyPriorTierShowsTildeOne(currentTierIndex: number, values: TierValueList = this.currencyTierValueList): boolean {
    for (let j = 1; j < currentTierIndex; j++) {
      if (this.tierRowShowsTildeOne(j, values)) return true;
    }
    return false;
  }
}
